import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  IconButton,
  CardMedia,
  Paper,
  Chip,
  Switch,
  Stack,
  Avatar,
  Tabs,
  Tab,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  TextField,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Share as ShareIcon,
  Chat as ChatIcon,
  LocationOn as LocationIcon,
  ArrowBackIos,
  ArrowForwardIos,
  Lock,
  CalendarToday as CalendarTodayIcon,
  Cancel as CancelIcon,
  Pause as PauseIcon,
  PlayArrow as PlayIcon,
  Delete as DeleteIcon,
  People as PeopleIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { request } from '../../../../Utils/request';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store/rootReducers';
import { paiseToRupees } from '../../../../Utils/currencyConverter';
import { getOrCreateChat } from '../../../../services/CommonApiCall';
import ShareModal from '../../../../Components/ShareModal';
import BottomModal from '../../../../Components/BottomModel';
import ActionButton from '../ActionButton';
import ImageCarousel from './ImageCarousel';
import { Batch, ITripDetail, IUserBooking ,IBatch} from './Types/types';



const TripDetail: React.FC = () => {
  const navigate = useNavigate();
  const locationParams = useLocation();
  const { id } = useParams<{ id: string }>();
  const user = useSelector((state: RootState) => state.user);
  // Get type from navigation state, query params, or default to 'trip'
  const searchParams = new URLSearchParams(locationParams.search);
  const type = locationParams?.state?.type ||
    searchParams.get('type') ||
    'trip';
  const paramsID = id;

  const isPhoneVerified = user?.user?.phone?.isVerified === true;

  // Check if phone is verified
  const [trip, setTrip] = useState<ITripDetail | null>(null);
  const [selectedDayIdx, setSelectedDayIdx] = useState(0);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const currentUser = useSelector((state: RootState) => state.user.user);
  const [userBooking, setUserBooking] = useState<IUserBooking | null>(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [cancelBatchDialogOpen, setCancelBatchDialogOpen] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState<IBatch | null>(null);
  const [isCancellingBatch, setIsCancellingBatch] = useState(false);
  const [cancellationReason, setCancellationReason] = useState('');
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [batches, setBatches] = useState(
    {
      from: '',
      to: '',
      groupSize: 0,
      maxGroupSizeAllowed: 1
    }
  )

  const openCancelBatchDialog = useCallback((batch: IBatch) => {
    setSelectedBatch(batch);
    setCancelBatchDialogOpen(true);
  }, []);


  const getTomorrowDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };
  const minDate = getTomorrowDate();


  useEffect(() => {
    if (!paramsID) return;

    const fetchTrip = async () => {
      if (type === 'itinerary') {
        const res = await request<{ success: boolean; data: ITripDetail; message: string }>(
          `/itineraries/${paramsID}`,
          {
            method: 'GET',
            authorization: true
          }
        ).then((res) => {
          setTrip(res.data);
        }).catch((err) => {
          console.error(err);
        });
      } else {
        const res = await request<{ success: boolean; data: ITripDetail; message: string }>(
          `/trips/${paramsID}`,
          {
            method: 'GET',
            authorization: true,
          }
        ).then((res) => {
          setTrip(res.data);
        }).catch((err) => {
          console.error(err);
        });
      }
    };

    fetchTrip();
  }, [paramsID]);

 

  const handleDayChange = (event: React.SyntheticEvent, newValue: number, isLocked: any) => {
    if (isLocked) return;
    setSelectedDayIdx(newValue);
  };

  const handlePrevImage = () => {
    if (trip?.files && trip.files.length > 0) {
      setCurrentImageIndex((prev) => (prev === 0 ? trip.files.length - 1 : prev - 1));
    }
  };

  const handleNextImage = () => {
    if (trip?.files && trip.files.length > 0) {
      setCurrentImageIndex((prev) => (prev === trip.files.length - 1 ? 0 : prev + 1));
    }
  };

  const handleShare = () => {
    if (!trip?._id) return;
    setShareModalOpen(true);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const hasAvailableBatches = useMemo(() => {
    if (type === 'itinerary') return true;
    if (!trip?.batches?.length) return false;
  
    const today = new Date().setHours(0,0,0,0);
  
    return trip.batches.some(batch => {
      const from = new Date(batch.from).setHours(0,0,0,0);
      return (
        from >= today &&
        (batch.groupSize ?? 0) < (batch.maxGroupSizeAllowed ?? 0) &&
        batch.status === 'active' && !batch.bookingPaused
      );
    });
  }, [trip?.batches, type]);
  

  const handleCancelBooking = async () => {
    if (!userBooking?._id) {
      alert('Booking ID not found');
      return;
    }

    setIsCancelling(true);

    try {
      await request(`/trips/booking/${userBooking._id}/cancel`, {
        method: 'POST',
        authorization: true,
        body: {
          reason: 'Cancelled by user'
        }
      });

      setCancelDialogOpen(false);
      alert('Booking cancelled successfully');

      // Refresh booking status
      setUserBooking({ ...userBooking, status: 'cancelled' });
    } catch (error: any) {
      console.error('Failed to cancel booking:', error);
      alert(error.message || 'Failed to cancel booking. Please try again.');
    } finally {
      setIsCancelling(false);
    }
  };


  const updateBatch = (field: keyof Batch, value: any) => {
    setBatches(prev => ({
      ...prev,
      [field]: value,
    }));
  };


  const handleCancelBatch = async () => {
    if (!selectedBatch?._id || !trip?._id) {
      alert('Batch ID not found');
      return;
    }

    // Only allow canceling batches for trips, not itineraries
    if (type === 'itinerary') {
      alert('Batch cancellation is only available for trips');
      return;
    }

    // Validate cancellation reason
    if (!cancellationReason.trim()) {
      alert('Please provide a reason for cancellation');
      return;
    }

    setIsCancellingBatch(true);
    try {
      const endpoint = `/trips/${trip?._id}/batch/${selectedBatch?._id}/cancel`;
      await request(endpoint, {
        method: 'POST',
        authorization: true,
        body: {
          reason: cancellationReason.trim()
        } as any
      });
      setCancelBatchDialogOpen(false);
      setCancellationReason('');
      alert('Batch cancelled successfully');
      // Reflect cancelled status immediately in UI.
      if (trip?.batches) {
        setTrip({
          ...trip,
          batches: trip.batches.map(batch =>
            batch._id === selectedBatch._id
              ? { ...batch, status: 'cancelled', bookingPaused: true }
              : batch
          )
        });
      }
      setSelectedBatch(null);
    } catch (error: any) {
      console.error('Failed to cancel batch:', error);
      alert(error.message || 'Failed to cancel batch. Please try again.');
    } finally {
      setIsCancellingBatch(false);
    }
  };

  if (!trip) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  const { title, description, notes, files, category, pricePerPerson, schedule, location, userId, user: tripUser, _id } = trip;

  const handleChatWithHost = async () => {
    if (!currentUser) {
      navigate('/login');
      return;
    }
    const currentUserId = user?.user?._id || user?.user?.id;
    const hostUserId = tripUser?._id || tripUser?.id;

    if (!currentUserId || !hostUserId) {
      console.warn('Cannot create chat: missing user IDs', { currentUserId, hostUserId });
      return;
    }

    try {
      const chatId = await getOrCreateChat(currentUserId, hostUserId);
      navigate(`/chat/${chatId}`, {
        state: {
          chatId,
          otherUserName: tripUser?._id || tripUser?.id,
          source: 'tripDetail',
        },
      });
    } catch (error) {
      console.error('Error creating chat with host:', error);
      // Optionally add user-facing notification
    }
  };

  const handleMakeCopy = () => {
    if (!trip) return;
    const tripTypeLower = trip.type?.toLowerCase();
    const formTripType = tripTypeLower === 'itinerary' ? 'Itinerary' : 'Trip';
    const copyPayload = {
      ...trip,
      _id: undefined,
      batches: (trip.batches || []).map(({ _id, ...b }) => b),
      files: (trip.files || []).map(({ fileKey, fileType, featured, fileURL }: any) => ({
        fileKey,
        fileType: fileType || 'image/jpeg',
        featured: !!featured,
        fileURL,
      })),
    };
    navigate('/createtrip', {
      state: {
        trip: copyPayload,
        isCopy: true,
        tripType: formTripType,
      },
    });
  };

  const featuredImage = files?.find((f) => f.featured)?.fileURL || files?.[0]?.fileURL || '';

  const handlePauseBatch = async (tripId: string, batchId: any, action: string) => {
    if (!tripId || !batchId) {
      alert('Booking ID not found');
      return;
    }

    try {
      const res: any = await request(`/trips/${tripId}/batch/${batchId}/${action}`, {
        method: 'POST',
        authorization: true,
      });
      alert(res.message ?? (action === 'pause' ? 'Trip paused.' : 'Trip resumed.'));
      // Refetch trip so button label updates to "Resume" / "Pause" and icon updates
      if (paramsID) {
        const endpoint = type === 'itinerary' ? `/itineraries/${paramsID}` : `/trips/${paramsID}`;
        const updated = await request<{ success: boolean; data: ITripDetail; message: string }>(endpoint, {
          method: 'GET',
          authorization: true,
        });
        if (updated?.data) setTrip(updated.data);
      }
    } catch (err: any) {
      alert(err?.message || 'Failed to pause/resume. Please try again.');
    }
  }
  const handleAddbatch = async () => {
    if (!batches.from || !batches.to || !batches.maxGroupSizeAllowed) {
      alert("All fileds are required!");
      return
    }
    try {
      const res: any = await request(`/trips/${_id}/batch`, {
        method: 'POST',
        authorization: true,
        body: batches
      });

      alert(res.message || 'Batch added successfully');
      setAddModalOpen(false);
      setBatches({
        from: '',
        to: '',
        groupSize: 0,
        maxGroupSizeAllowed: 1
      });

      // Refresh trip data so newly created batch appears immediately.
      const endpoint = type === 'itinerary' ? `/itineraries/${paramsID}` : `/trips/${paramsID}`;
      const updated = await request<{ success: boolean; data: ITripDetail; message: string }>(endpoint, {
        method: 'GET',
        authorization: true,
      });
      if (updated?.data) {
        setTrip(updated.data);
      }
    } catch (err: any) {
      alert(err.message || 'Failed to Add batches. Please try again.');
    }
  }

// status 'on_hold' → 'On Hold', 'active' → 'Active'
function statusToLabel(status: string): string {
  if (!status) return '';
  return status
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}





  return (
    <Box sx={{}}>

      <Container maxWidth="sm" sx={{ py: 3 }}>
        <Paper sx={{ borderRadius: 3, overflow: 'hidden', mb: 3, backgroundColor: '#fff8f5' }}>
          {/* Image */}
          <ImageCarousel images={files} />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2, pt: 1 }}>
            <IconButton
              onClick={handleShare}
              sx={{
                color: '#49454F',
                bgcolor: '#FFFFFF',
                border: '1px solid #E0E0E0',
                width: 36,
                height: 36,
                '&:hover': {
                  bgcolor: '#F7F7F7',
                },
              }}
              aria-label="Share trip"
            >
              <ShareIcon fontSize="small" />
            </IconButton>
          </Box>
          <Box sx={{ p: 2 }}>
            <Typography fontWeight="bold" gutterBottom sx={{ fontSize: '16px', color: '#1D1B20' }}>
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontSize: '12px' }}>
              {description}
            </Typography>

            {/* Categories */}
            <Stack direction="row" spacing={1} sx={{ my: '1px' }}>
              {category.map((c) => (
                // <Chip key={c} label={c} size="small" />
                <Typography key={c} variant="body1" color="#49454F" sx={{ fontSize: '14px', fontWeight: '400' }}>{c}</Typography>
              ))}
            </Stack>

            {/* Price & Duration */}
            <Typography sx={{ mt: '1px', color: '#381E72', fontWeight: '500', fontSize: '14px' }}>
              ₹{paiseToRupees(pricePerPerson).toFixed(2)} {type !== 'itinerary' && '/person'} • {schedule.dailySchedule.length} Days
            </Typography>

            {/* Location */}
            {location?.name && (
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: '1px' }}>
                <LocationIcon fontSize="small" sx={{ color: '#4FC3F7', fontSize: '16px' }} />
                <Typography variant="body2" color="#4FC3F7" sx={{ fontSize: '16px' }}>
                  {location.name}
                </Typography>
              </Stack>
            )}

            {/* Chat with host */}
            <Divider sx={{ my: '8px' }} />
            {tripUser && (
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                onClick={handleChatWithHost}
                sx={{
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.02)',
                  },
                  borderRadius: 1,
                  px: 1,
                  py: 0.5,
                }}
              >
                <ChatIcon sx={{ fontSize: '16px', color: '#49454F' }} />
                <Typography variant="body2" color="#757575" sx={{ fontSize: '16px' }}>Chat with Host</Typography>
                <Box sx={{ flexGrow: 1 }} />
                <Chip label={tripUser.username} size="small" sx={{ backgroundColor: '#ECE0DA99', borderRadius: 1, borderWidth: 2, borderColor: '#red' }} />
              </Stack>
            )}
            <Divider sx={{ my: '8px' }} />

            {/* Note section */}
            {notes && (
              <Box sx={{ mt: 2 }}>
                <Typography color="#49454F" sx={{ fontSize: '14px' }}>
                  Note-
                </Typography>{' '}
                <Typography color="#49454F" sx={{ fontSize: '14px' }}>
                  {notes}
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>

        {/* Available Batches Section - only visible to trip creator */}
        {trip?.batches && trip.batches.length > 0 && currentUser?.username === tripUser?.username && (
          <Paper sx={{ borderRadius: 3, overflow: 'hidden', mb: 3, p: 2 }}>
            <Box sx={{ flexDirection: 'row', display: 'flex', gap: 3, alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                Available Batches
              </Typography>
              {currentUser?.username === tripUser?.username &&
              <Box onClick={() => {
                // handleAddbatch('xys')
                setAddModalOpen(true)
              }}>
                <Typography variant="body1" fontWeight="bold" sx={{ mb: 2, color: '#FEB380' }}>
                  + Add Batch
                </Typography>
              </Box>
            }
            </Box>
            <Stack spacing={2}>
              {trip.batches.map((batch, index) => (
                <Box
                  key={batch._id || index}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: '#FAFAFA',
                    border: '1px solid #E0E0E0',
                  }}
                >
                  <Stack spacing={1.5}>
                    {/* Date Range */}
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <CalendarTodayIcon sx={{ color: '#666', fontSize: 18 }} />
                      <Typography variant="body1" sx={{ color: '#333', fontWeight: 500 }}>
                        {formatDate(batch.from)} - {formatDate(batch.to)}
                      </Typography>
                    </Stack>

                    {/* Participants Count */}
                    {(batch.groupSize !== undefined || batch.maxGroupSizeAllowed !== undefined) && (
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        mt={1}
                      >
                        {/* Participants */}
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <PeopleIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
                          <Typography variant="body2" color="text.secondary">
                            {batch.groupSize ?? 0} / {batch.maxGroupSizeAllowed ?? 0} participants
                          </Typography>
                        </Stack>

                        {/* Action Buttons */}
                        {currentUser?.username === tripUser?.username &&
                          batch._id &&
                          type !== 'itinerary' && (
                            <Stack direction="row" spacing={1.5}>
                              {batch.status === 'active' &&
                                <ActionButton
                                  actionType={batch.bookingPaused ? 'resume' : 'pause'}
                                  label={batch?.bookingPaused ? 'Resume' : 'Pause'}
                                  icon={batch?.bookingPaused ? <PlayIcon /> : <PauseIcon />}
                                  onClick={() => {
                                    const action = batch?.bookingPaused ? 'unpause' : 'pause'
                                    handlePauseBatch(_id, batch?._id, action)
                                  }}
                                />
                              }
                              <ActionButton
                                actionType='cancel'
                                disabled={batch.status === 'on_hold' || batch.status === 'cancelled'}
                                label={batch.status === 'active' ? 'Cancel' :  statusToLabel(batch?.status ?? '')}
                                icon={<CancelIcon />}
                                onClick={() => openCancelBatchDialog(batch)}
                              />
                            </Stack>
                          )}
                      </Stack>
                    )}


                    {/* Cancel Batch Button - only for trip owner and only for trips (not itineraries) */}

                  </Stack>
                </Box>
              ))}
            </Stack>
          </Paper>
        )}

        {/* User Booking Status Section */}
        {userBooking && (
          <Paper sx={{ borderRadius: 3, overflow: 'hidden', mb: 3, p: 2 }}>
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
              Your Booking
            </Typography>

            {/* Booking Status Badge */}
            <Chip
              label={userBooking.status === 'cancelled' ? 'CANCELLED' : userBooking.status.toUpperCase()}
              sx={{
                mb: 2,
                backgroundColor: userBooking.status === 'cancelled' ? '#FFEBEE' : '#E8F5E9',
                color: userBooking.status === 'cancelled' ? '#C62828' : '#2E7D32',
                fontWeight: 'bold',
              }}
            />

            {/* Booking Details */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                Travel Dates
              </Typography>
              <Typography variant="body1" fontWeight="500">
                {formatDate(userBooking.batchData.from)} - {formatDate(userBooking.batchData.to)}
              </Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                Travelers
              </Typography>
              <Typography variant="body1" fontWeight="500">
                {userBooking.quantity} Person{userBooking.quantity > 1 ? 's' : ''}
              </Typography>
            </Box>

            {/* Cancel Button or Cancelled Status */}
            {userBooking.status === 'cancelled' ? (
              <Box sx={{ mt: 2, p: 2, backgroundColor: '#FFEBEE', borderRadius: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <CancelIcon sx={{ color: '#C62828' }} />
                  <Typography variant="body2" sx={{ color: '#C62828', fontWeight: 500 }}>
                    This booking has been cancelled
                  </Typography>
                </Stack>
              </Box>
            ) : (
              <Button
                fullWidth
                variant="outlined"
                color="error"
                startIcon={<CancelIcon />}
                onClick={() => {
                  setCancelDialogOpen(true)
                }
                }
                sx={{
                  mt: 2,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 'bold',
                }}
              >
                Cancel Booking
              </Button>
            )}
          </Paper>
        )}

        {/* What you'll do */}
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          What you'll do
        </Typography>

        {/* Day tabs */}
        <Tabs
          value={selectedDayIdx}
          onChange={(event, newValue) => {
            const isLocked = 'locked' in schedule.dailySchedule[newValue];
            handleDayChange(event, newValue, isLocked)
          }}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            '& .MuiTabs-flexContainer': {
              gap: 1,
            },
            '& .MuiTab-root': {
              textTransform: 'none',
              borderRadius: 3,
              minWidth: 80,
              fontWeight: 'bold',
              fontSize: '0.875rem',
              color: '#263238',
              backgroundColor: 'white',
              border: '1px solid #E0E0E0',
              padding: '8px 16px',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                backgroundColor: '#F5F5F5',
              }
            },
            '& .Mui-selected': {
              backgroundColor: '#FEB380',
              color: '#263238 !important',
              border: '1px solid #FEB380',
              '&:hover': {
                backgroundColor: '#FEB380',
              }
            },
            '& .MuiTabs-indicator': {
              display: 'none', // Hide the default indicator
            }
          }}
        >
          {schedule.dailySchedule.map((day, idx) => {
            const isLocked = 'locked' in day;
            return isLocked ? <Tab key={idx} icon={<Lock />} /> : <Tab key={idx} label={`DAY ${idx + 1}`} />;
          })}
        </Tabs>

        <Divider sx={{ my: 2 }} />

        {/* Tasks for selected day */}
        <Stack spacing={2}>
          {schedule.dailySchedule[selectedDayIdx]?.tasks.map((task, idx) => (
            <Paper key={idx} sx={{ p: 1.5, borderRadius: 2, display: 'flex', gap: 2 }} elevation={1}>
              <Avatar
                variant="rounded"
                src={task.file?.fileURL || ''}
                sx={{ width: 64, height: 64, borderRadius: '6px' }}
              />
              <Box>
                <Typography variant="subtitle2" fontWeight="bold">
                  {task.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {task.description}
                </Typography>
              </Box>
            </Paper>
          ))}
        </Stack>
        {currentUser?.username !== tripUser?.username && (type !== 'itinerary' || !trip?.hasPurchased?._id) && (
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Button
              variant="contained"
              disabled={!hasAvailableBatches}
              onClick={() => {
                if (!currentUser) {
                  navigate('/login');
                  return;
                }
                // Check if phone is verified before allowing booking
                if (!isPhoneVerified) {
                  alert('Please verify your phone number to continue with booking.');
                  navigate('/phone-verification');
                  return;
                }
                // If phone is verified, proceed to checkout
                navigate(`/trips/${paramsID}/checkout`, { state: { tripData: trip, type: type } });
              }}
              sx={{
                mt: 2,
                borderRadius: 12,
                backgroundColor: '#ff6b35',
                color: 'white',
                width: '100%',
                borderWidth: 2,
                borderColor: '#red',
                '&:disabled': {
                  backgroundColor: '#ccc',
                  color: '#666',
                }
              }}
            >
              {type !== 'itinerary' ? 'Book Now' : 'Buy Itenary'}
            </Button>
          </Box>
        )}

        {currentUser?.username === tripUser?.username && type !== 'itinerary' && (
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Button
              variant="contained"
              onClick={handleMakeCopy}
              sx={{
                mt: 2,
                borderRadius: 12,
                backgroundColor: '#ff6b35',
                color: 'white',
                width: '100%',
                borderWidth: 2,
                borderColor: '#red',
                '&:disabled': {
                  backgroundColor: '#ccc',
                  color: '#666',
                }
              }}
            >
              Make a copy
            </Button>
          </Box>
        )}

      </Container>

      {/* Cancel Booking Confirmation Dialog */}
      <Dialog
        open={cancelDialogOpen}
        onClose={() => !isCancelling && setCancelDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 'bold', color: '#263238' }}>
          Cancel Booking?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ color: '#757575' }}>
            Are you sure you want to cancel this booking? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={() => setCancelDialogOpen(false)}
            disabled={isCancelling}
            sx={{
              textTransform: 'none',
              color: '#757575',
              fontWeight: 'bold'
            }}
          >
            No, Keep Booking
          </Button>
          <Button
            onClick={() => handleCancelBooking()}
            variant="contained"
            color="error"
            disabled={isCancelling}
            startIcon={isCancelling ? <CircularProgress size={20} color="inherit" /> : <CancelIcon />}
            sx={{
              textTransform: 'none',
              fontWeight: 'bold',
              px: 3
            }}
          >
            {isCancelling ? 'Cancelling...' : 'Yes, Cancel'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Batch Confirmation Dialog */}
      <Dialog
        open={cancelBatchDialogOpen}
        onClose={() => {
          if (!isCancellingBatch) {
            setCancelBatchDialogOpen(false);
            setSelectedBatch(null);
            setCancellationReason('');
          }
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
          }
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 'bold',
            color: '#000',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pb: 1
          }}
        >
          Cancel Batch
          <IconButton
            onClick={() => {
              if (!isCancellingBatch) {
                setCancelBatchDialogOpen(false);
                setSelectedBatch(null);
                setCancellationReason('');
              }
            }}
            disabled={isCancellingBatch}
            sx={{
              p: 0.5,
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              }
            }}
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {/* Batch Information Box */}
          {selectedBatch && (
            <Box
              sx={{
                p: 2,
                mb: 3,
                backgroundColor: '#FAFAFA',
                borderRadius: 2,
                border: '1px solid #E0E0E0',
              }}
            >
              <Stack spacing={1.5}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <CalendarTodayIcon sx={{ color: '#666', fontSize: 18 }} />
                  <Typography variant="body1" sx={{ color: '#333', fontWeight: 500 }}>
                    {formatDate(selectedBatch.from)} - {formatDate(selectedBatch.to)}
                  </Typography>
                </Stack>
                {(selectedBatch.groupSize !== undefined || selectedBatch.maxGroupSizeAllowed !== undefined) && (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <PeopleIcon sx={{ color: '#666', fontSize: 18 }} />
                    <Typography variant="body2" sx={{ color: '#666' }}>
                      {selectedBatch.groupSize || 0} / {selectedBatch.maxGroupSizeAllowed || 0} participants
                    </Typography>
                  </Stack>
                )}
              </Stack>
            </Box>
          )}

          {/* Reason for Cancellation */}
          <Typography variant="body2" sx={{ color: '#333', fontWeight: 500, mb: 1 }}>
            Reason for Cancellation *
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={5}
            placeholder="Enter reason for cancelling this batch..."
            value={cancellationReason}
            onChange={(e) => setCancellationReason(e.target.value)}
            disabled={isCancellingBatch}
            required
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={() => {
              setCancelBatchDialogOpen(false);
              setSelectedBatch(null);
              setCancellationReason('');
            }}
            disabled={isCancellingBatch}
            variant="outlined"
            sx={{
              textTransform: 'none',
              color: '#333',
              fontWeight: 'bold',
              borderColor: '#E0E0E0',
              backgroundColor: 'white',
              px: 3,
              '&:hover': {
                borderColor: '#BDBDBD',
                backgroundColor: '#FAFAFA',
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCancelBatch}
            variant="contained"
            disabled={isCancellingBatch || !cancellationReason.trim()}
            sx={{
              textTransform: 'none',
              fontWeight: 'bold',
              px: 3,
              backgroundColor: '#E53935',
              color: 'white',
              '&:hover': {
                backgroundColor: '#C62828',
              },
              '&:disabled': {
                backgroundColor: '#FFCDD2',
                color: 'white',
              }
            }}
          >
            {isCancellingBatch ? (
              <>
                <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                Cancelling...
              </>
            ) : (
              'Confirm Cancel'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <ShareModal
        open={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        title={trip?.title || 'Trip'}
        shareUrl={trip?._id ? `${process.env.NODE_ENV === 'production' ? 'https://ifinds.in' : window.location.origin.replace('https://', 'http://')}/trips/${trip._id}${type === 'itinerary' ? '?type=itinerary' : ''}` : ''}
        shareText={trip?.description || `Check out this ${type === 'itinerary' ? 'itinerary' : 'trip'}: ${trip?.title || ''}`}
      />
      <BottomModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        title="Add Batch"
        maxHeight="70vh"
      >
        <Box sx={{ margin: '16px' }}>

          <Box sx={{ mb: 3 }}>
            {/* Header */}
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              mb={2}
            >

            </Stack>

            {/* Dates */}
            <Stack direction="row" spacing={2} mb={2}>
              <TextField
                fullWidth
                type="date"
                label="Start Date"
                value={batches.from.split('T')[0] || ''}
                onChange={(e) =>
                  updateBatch(
                    'from',
                    `${e.target.value}T00:00:00.000Z`
                  )
                }
                // onBlur={() =>
                //   markFieldTouched(`batch_${batchIndex}_from`)
                // }
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: minDate }}
                required
              // error={
              //   shouldShowError(`batch_${batchIndex}_from`) &&
              //   (!batch.from ||
              //     new Date(batch.from).setHours(0, 0, 0, 0) <=
              //     new Date().setHours(0, 0, 0, 0))
              // }
              // helperText={
              //   shouldShowError(`batch_${batchIndex}_from`) &&
              //     (!batch.from ||
              //       new Date(batch.from).setHours(0, 0, 0, 0) <=
              //       new Date().setHours(0, 0, 0, 0))
              //     ? 'Start date must be after today'
              //     : ''
              // }
              />

              <TextField
                fullWidth
                type="date"
                label="End Date"
                value={batches.to.split('T')[0] || ''}
                onChange={(e) =>
                  updateBatch(
                    'to',
                    `${e.target.value}T00:00:00.000Z`
                  )
                }
              
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: minDate }}
                required
              />
            </Stack>

            {/* Group Size */}
            <TextField
              fullWidth
              type="number"
              label="Max Group Size Allowed"
              value={batches.maxGroupSizeAllowed || ''}
              onChange={(e) =>
                updateBatch(
                  'maxGroupSizeAllowed',
                  Number(e.target.value)
                )
              }
              inputProps={{ min: 1 }}
              required
            />

          </Box>

          <Button title='Add'
            variant="contained"
            fullWidth
            sx={{ height: '40px', color: '#fff', backgroundColor: '#FF6B6B', }}
            onClick={() => handleAddbatch()} >Add</Button>

        </Box>

      </BottomModal>
    </Box>
  );
};

export default TripDetail;

